import { defineMetrics } from "../types.js"

/**
 * ⚠ **The last three count only `type: 'bundle'` packages.** `Package.type` is
 * `bundle | unlimited | waived`, and `bundleSize` is meaningful only for bundles: "how many are
 * left" is **infinite** for unlimited and **not applicable** for waived, and both carry the
 * default `bundleSize` of 0. Including them drags both "included" and "remaining" down — an
 * unlimited customer who has used 30 applications would show "-30 remaining", which is a
 * fabricated alarm about a limit that does not exist. v1 filters the same way.
 * ⚠ Packages hang **off the user**: v1 looks them up through
 * `user.applicationUsageControl.packages` rather than from `Package.owner`. The two paths agree
 * on clean data and disagree on dirty data — we follow v1's, or the same deployment would report
 * two different quota numbers in two places.
 */
export const packagesMetrics = defineMetrics([
  {
    id: "package_type",
    name: "Package Type",
    description: "Type of package purchased by the client.",
    logic: "Retrieve package configuration assigned to the client.",
    source: "package.type, one of bundle | unlimited | waived",
    domain: "packages",
    srvModule: "packages",
    priority: "nice",
    valueType: "count",
    dims: ["class"],
    rollup: "last",
    defaultWidget: "pie",
    altWidgets: ["table", "hbar"],
    coveredByV1: false,
    xlsxRow: 38,
  },
  {
    id: "applications_included",
    name: "Applications Included",
    description: "Total applications included in the purchased package.",
    logic: "Retrieve package allocation value.",
    source: "sum(package.bundleSize) over bundle packages only",
    domain: "packages",
    srvModule: "packages",
    priority: "nice",
    valueType: "count",
    dims: [],
    rollup: "last",
    defaultWidget: "stat-tile",
    coveredByV1: true,
    caveat:
      "Counts bundle packages only. bundleSize is meaningless for the unlimited and waived types, whose default is 0, so including them would understate the allocation. Matches v1's totalSize.",
    xlsxRow: 39,
  },
  {
    id: "applications_used",
    name: "Applications Used",
    description: "Number of applications created from the purchased package.",
    logic: "Count applications associated with the package.",
    source: "sum($size(package.applicationIds)) over bundle packages only",
    domain: "packages",
    srvModule: "packages",
    priority: "nice",
    valueType: "count",
    dims: [],
    rollup: "last",
    defaultWidget: "stat-tile",
    altWidgets: ["gauge", "stacked-bar"],
    coveredByV1: false,
    caveat:
      "Taken from the length of the package's applicationIds array, which is what v1 counts as appliedSize. Application.usagePackageId points the other way and is not used here, because two sources for one number can disagree.",
    xlsxRow: 40,
  },
  {
    id: "applications_remaining",
    name: "Applications Remaining",
    description: "Remaining applications available within the package.",
    logic: "Applications Included − Applications Used.",
    source: "sum(package.bundleSize) - sum($size(package.applicationIds)), bundle packages only",
    domain: "packages",
    srvModule: "packages",
    priority: "must",
    valueType: "count",
    dims: [],
    rollup: "last",
    defaultWidget: "gauge",
    altWidgets: ["stat-tile", "stacked-bar"],
    coveredByV1: false,
    caveat:
      "Bundle packages only, so a deployment on an unlimited or waived plan reports empty rather than a number. Including those types would compute a negative remainder for a customer who has no limit at all, which reads as an alarm.",
    xlsxRow: 41,
  },
])
